import { ReactElement, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PRIVATE_COMPETITIONS } from 'utils/constants/sportsbook/private-competitions';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { ResultsGame } from 'interfaces/results';
import { FallbackContainer } from 'components/UI/Containers/FallbackContainer';
import { Viewport } from 'components/Viewport';
import { getIsLoggedIn } from 'store/selectors/user-data';
import { useGetActiveCompetitions } from '../hooks/useGetActiveCompetitions';
import { useGetResultGames } from '../hooks/useGetResultGames';

if (SpringConfigs.IS_RTL) {
  import('../rtl.less');
}

const { ResultsMobileView } = lazy(
  () => import(/* webpackChunkName: "results-mobile-view" */ './Mobile')
);

const { ResultsDesktopView } = lazy(
  () => import(/* webpackChunkName: "results-desktop-view" */ './Desktop')
);

type Props = {
  configs: {
    resultsSportType?: string;
  };
};

export const ResultsView = (props: Props): ReactElement => {
  const isLoggedIn = useSelector(getIsLoggedIn);

  const resultsSportType = useMemo(() => {
    return props.configs.resultsSportType !== 'all'
      ? props.configs.resultsSportType
      : null;
  }, [props.configs.resultsSportType]);

  useGetActiveCompetitions(resultsSportType);

  const [games, setGames] = useState<ResultsGame[] | null>(null);
  const loading = useGetResultGames({
    setGames,
    games,
    resultsSportType
  });

  const gamesWithHiddenCheck = useMemo(() => {
    const privateCompetitionIds =
      PRIVATE_COMPETITIONS[SpringConfigs.JURISDICTION];

    if (privateCompetitionIds && games && !isLoggedIn) {
      return games.map(game => {
        if (privateCompetitionIds.includes(game.competition_id)) {
          const gameCopy = { ...game };
          gameCopy.scores = '-';
          gameCopy.date = 0;

          return gameCopy;
        } else {
          return game;
        }
      });
    } else {
      return games;
    }
  }, [games, isLoggedIn]);

  return (
    <Viewport
      mobile={ResultsMobileView}
      desktop={ResultsDesktopView}
      innerProps={{
        games: gamesWithHiddenCheck,
        loading
      }}
      fallback={<FallbackContainer />}
    />
  );
};
